import { ColDef } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import ActionsColumn from 'components/tables/data/remote-source/columns/ActionsColumn';
import NameColumn from 'components/tables/data/remote-source/columns/NameColumn';
import RemoteSourceIdColumn from 'components/tables/data/remote-source/columns/RemoteSourceIdColumn';
import TypeColumn from 'components/tables/data/remote-source/columns/TypeColumn';
import UrlColumn from 'components/tables/data/remote-source/columns/UrlColumn';
import UserColumn from 'components/tables/data/remote-source/columns/UserColumn';
import { RemoteSourceRow } from 'components/tables/data/remote-source/types';
import DataTable from 'components/tables/DataTable';
import { hiddenSearchableColumn as hiddenSearchable } from 'components/tables/utils/hidden-searchable-column';
import { useCallback, useMemo } from 'react';
import { ADD_REMOTE_SOURCE_PAGE_PATH } from 'routes/paths';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Table which displays remote sources.
 */
export default function RemoteSourceTable() {
  const { getAction } = useApi();
  const { tableActionMode } = useResponsiveDesign();

  const action = useCallback(async () => {
    const getRemoteSources = getAction('RemoteSourceList');
    const remoteSources = await getRemoteSources();
    return remoteSources;
  }, [getAction]);

  const dataExport = useMemo(
    () => ({
      modalTitle: 'Exportovať externé zdroje',
      fileName: 'externe-zdroje.xlsx',
      columnKeys: [
        'remoteSourceId',
        'name',
        'type',
        'url',
        'login',
        'incomingApiKey',
        'user.userId',
        'user.fullName',
        'status',
      ],
    }),
    []
  );

  const columns = useMemo<ColDef[]>(
    () => [
      // ANCHOR Visible Columns
      {
        field: 'remoteSourceId',
        headerName: 'ID',
        sortable: true,
        unSortIcon: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        cellRenderer: RemoteSourceIdColumn,
      },
      {
        field: 'name',
        headerName: 'Názov',
        minWidth: 250,
        sortable: true,
        filter: true,
        unSortIcon: true,
        cellRenderer: NameColumn,
      },
      {
        field: 'type',
        headerName: 'Typ',
        minWidth: 150,
        sortable: true,
        filter: true,
        unSortIcon: true,
        cellRenderer: TypeColumn,
      },
      {
        field: 'url',
        headerName: 'URL',
        minWidth: 250,
        cellRenderer: UrlColumn,
      },
      {
        field: 'user.fullName',
        headerName: 'Importujúci používateľ',
        minWidth: 180,
        cellRenderer: UserColumn,
      },

      // ANCHOR Hidden Columns
      { field: 'login', headerName: 'Login', ...hiddenSearchable },
      { field: 'incomingApiKey', headerName: 'API kľúč', ...hiddenSearchable },
      { field: 'user.userId', headerName: 'ID užívateľa', ...hiddenSearchable },

      // ANCHOR Status
      {
        field: 'status',
        headerName: 'Aktívny',
        valueGetter: ({ data: { status } }: { data: RemoteSourceRow }) => Number(status),
        hide: true,
        filter: true,
      },

      // ANCHOR Actions
      {
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(244, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(244, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(244, tableActionMode),
        lockPosition: 'right',
        cellRenderer: ActionsColumn,
      },
    ],
    [tableActionMode]
  );

  return (
    <DataTable
      tableId="remote-sources"
      title="Externé zdroje"
      addButtonText="Pridať nový"
      toggleDiscardedLabel="Zobraziť neaktívne"
      columns={columns}
      action={action}
      addButtonTarget={ADD_REMOTE_SOURCE_PAGE_PATH.original}
      dataExport={dataExport}
    />
  );
}
